import {
    BlueText,
    InputInfoLabelSubTitle,
    InputInfoLabelTitle,
    textAreaStyles
} from '../styles'
import { LabelsForInput } from 'components/LabelsForInputs'
import { InputSelect } from 'components/InputSelect'
import { RiskEventRadio } from './RiskEventRadio'
import { CustomDatePicker } from 'components/Datepicker'
import { InputLabel } from 'components/InputLabel'
import { NotificationType } from 'types/notification'
import {
    Control,
    FieldErrors,
    FieldValues,
    UseFormRegister,
    UseFormResetField,
    UseFormSetValue,
    UseFormWatch
} from 'react-hook-form'
import { NotificationGuidance } from './NotificationGuidance'
import { IAuthenticationReducer } from 'types/autentication'
import { TextArea } from 'components/TextArea'
import { listOfRoles } from 'components/WithRoleAuthorization/WithRoleAuthorization'
import { IVariableReducer } from 'types/variable'
import { ILocation, ILocationReducer } from 'types/location'
import { RootReducerState } from 'types/global'
import { SectorReducer } from 'types/sector'
import { useDispatch, useSelector } from 'react-redux'
import { CustomToggle } from 'components/ToggleYesOrNot/CustomToggle'
import {
    incidentSectorList,
    locationsIcidentList,
    setDefaultNotificationValues,
    shiftIncidentList,
    variableListNumberPatientsInvolved,
    variableSelectorOptions,
    variablesListContributingFactor,
    variablesListDamageLevel
} from 'pages/NotificationTreatment/NotificationsTreatmentsSteps/NotificationData/helper'
import { occurrencesList, calculateObjectProbabilityOcurrence } from 'pages/NotificationTreatment/helpers'
import { useEffect, useCallback, useMemo } from 'react'
import { isModerateOrSevereOrDeathDamage, requiresSubDamageLevel } from 'utils/damage'
import { InvolvesPatient } from './InvolvesPatient'
import { InvolvedPatients } from 'pages/NotifyEvent/components/form/InvolvedPatients'
import { Multiplier } from 'pages/NotifyEvent/components/form/Multiplier'
import { NotifyUpdateEventType } from 'validator/notify_complementary_infos'
import { useState } from 'react'
import YellowCircleInterrogation from 'components/Icons/YellowCircleInterrogation'
import DefaultModal from 'components/Modals'
import ProbabilityOccurenceModal from 'pages/NotifyEvent/components/modals/ProbabilityOccurenceModal'
import DefaultMobileModal from 'components/MobileModal/MobileModal'
import { useIsMobileScreen } from 'hooks/useMediaQuery'
import NotificationActions from 'actions/Notification'
import LocationActions from 'actions/Location'
import { NotificationReducer } from 'types/notification'
import { Label } from 'components/TextArea/styles'
import { HelpDescription, inputRequiredStyle } from '../styles'
import { NeverEventsOrSentinelEventRadio } from './NeverEventsOrSentinelEventRadio'
import { GenericRadioGroup } from './GenericRadioGroup'
import SubDamageLevelInfoModal from 'pages/NotifyEvent/components/modals/SubDamageLevelInfoModal'
import { SUB_DAMAGE_LEVEL_OPTIONS } from 'utils/subdamageEnum'

interface FormInputsProps {
    notification: NotificationType
    watch: UseFormWatch<FieldValues>
    register: UseFormRegister<FieldValues>
    control: Control<FieldValues, any>
    statusIsPendingFinishOrFinished: boolean
    errors: FieldErrors<FieldValues>
    auth: IAuthenticationReducer
    canEditStepOne: boolean
    setValue: UseFormSetValue<FieldValues>
    dirtyFields: Partial<FieldValues>
    resetField: UseFormResetField<FieldValues>
    variableSelector: IVariableReducer
    loggedInUserIsResponsible?: boolean
    canEditGuidanceField?: boolean
    showTopSectionOnly?: boolean
}

export const FormInputs = ({
    notification,
    watch,
    control,
    errors,
    register,
    statusIsPendingFinishOrFinished,
    auth,
    canEditStepOne,
    setValue,
    dirtyFields,
    variableSelector,
    loggedInUserIsResponsible = true,
    canEditGuidanceField = false,
    showTopSectionOnly = false
}: FormInputsProps) => {
    const sectorSelector: SectorReducer = useSelector(
        (state: RootReducerState) => state.sectorReducers
    )

    const notificationSelector: NotificationReducer = useSelector(
        (state: RootReducerState) => state.notificationsReducer
    )

    const [activeLocations, setActiveLocations] = useState<ILocation[]>([])
    const [listCountLastReclassifiedIncident, setListCountLastReclassifiedIncident] = useState<any[]>()
    const [countLastReclassifiedIncident, setCountLastReclassifiedIncident] = useState<string>(null)
    const [sentinelEvent, setSentinelEvent] = useState([]);
    const [neverEvents, setNeverEvents] = useState([]);

    const [showProbabilityOccurenceModal, setShowProbabilityOccurenceModal] = useState(false)
    const [showSubdamageLevelInfoModal, setShowSubdamageLevelInfoModal] = useState(false)
    const [showSubDamageLevel, setShowSubDamageLevel] = useState<boolean>(false);
    const [probabiltyHasChanged, setProbabiltyHasChanged] = useState<boolean>(false)


    const involvesPatient = watch('involves_patient')
    const locationIncident = watch('location_incident')
    const uReclassifiedIncidentType = watch('u_reclassified_incident_type')
    const uOmsClassification = watch('u_oms_classification')
    const damageLevel = watch('damage_level')
    const uSubDamageLevel = watch('u_sub_damage_level')
    const uProbabilityOccurence = watch('u_probability_occurence')

    const uNeverOrSentinelEvents = watch('u_never_events_or_sentinel_event')
    const uNeverEventsReference = watch('u_never_events_reference')
    const uSentinelEventJci = watch('u_sentinel_event_jci')

    const watchFormData = watch()

    const isMobile = useIsMobileScreen()

    useEffect(() => {
        LocationActions.getActiveLocations().then(setActiveLocations)
    }, [])

    const locationOptions = useMemo(() => {
        const options = locationsIcidentList({
            locationsList: activeLocations
        } as ILocationReducer)
        const currentLocation = notification?.location_incident

        if (
            currentLocation?.value &&
            !options?.some(option => option.value === currentLocation.value)
        ) {
            return [
                ...(options || []),
                {
                    value: currentLocation.value,
                    label: currentLocation.display_value || currentLocation.value
                }
            ]
        }

        return options
    }, [activeLocations, notification?.location_incident])

    const isProfileCorporateQualityBoard = useMemo(() => {
        return auth?.dataJwt?.profile === listOfRoles.corporate_quality_board
    }, [auth?.dataJwt?.profile])

    // Verifica se o campo deve ser somente leitura baseado no perfil OU no status da notificação OU se não é o responsável
    const isReadOnly = useMemo(() => {
        // Nos status "Nova" e "Aguardando", não exige que seja responsável
        const notificationStatusState = notification?.u_state?.display_value || ''
        const isNewOrWaitingStatus = notificationStatusState === 'Nova' || notificationStatusState === 'Aguardando'

        // Se for status Nova/Aguardando e o usuário for admin, location_qa ou corporate_quality_board, permite editar
        if (isNewOrWaitingStatus) {
            // Se pode editar no status novo, só bloqueia se for corporate_quality_board ou status finalizado
            return isProfileCorporateQualityBoard || statusIsPendingFinishOrFinished
        }

        // Para outros status, mantém a lógica original
        return isProfileCorporateQualityBoard || statusIsPendingFinishOrFinished || !loggedInUserIsResponsible
    }, [isProfileCorporateQualityBoard, statusIsPendingFinishOrFinished, loggedInUserIsResponsible, notification, auth?.dataJwt?.profile])

    const setLocalEnabled = (b: boolean) => {
        setValue('involves_patient', b, { shouldDirty: true })
    }

    // const showMultiplierInput =
    //     watch('u_number_patients_involved') ===
    //     variableListNumberPatientsInvolved(variableSelector)?.choices?.[2]
    //         ?.value && watch('involves_patient')
    const notificationDispatch = useDispatch()

    //Declara os valores iniciais dos campos de Data
    /**
     * Controla a visualização da Orientação da notificação de acordo com o risco de imagem
     */
    useEffect(() => {
        setDefaultNotificationValues(setValue, notification, watch)

        // Se a notificação não está arquivada, garantir que os campos de arquivamento estejam limpos
        if (notification && notification?.u_state?.display_value !== 'Arquivada') {
            setValue('u_reason_for_archiving', '')
            setValue('u_archiving_observations', '')
        }

        if (notification && notification.location_incident && notification?.location_incident?.value != ``) {
            /* Busco o numero de ocorrencias em 30 dias agrupado o quantitativo usando o campo `u_reclassified_incident_type` e filtrando pela unidade atual da notificação */
            NotificationActions.getCountLastReclassifiedIncidentType(notificationDispatch, notification?.location_incident?.value)
            if (
                notification?.u_probability_occurence &&
                notification?.u_version?.value?.toLowerCase() !== 'v1'
            ) {
                setValue('u_probability_occurence', notification?.u_probability_occurence?.value)
            } else if (notification?.u_version?.value?.toLowerCase() === 'v1') {
                setValue('u_probability_occurence', '')
            }
        }
    }, [notification])

    // Reseta os selects de setores caso a unidade seja trocada
    useEffect(() => {
        if (dirtyFields?.location_incident) {
            setValue('incident_sector', '')
            setValue('reporter_sector', '')
        }
    }, [locationIncident])


    const onChangeDamageLevel = useCallback((value) => {
        setValue("damage_level", value, { shouldDirty: true });
    }, []);

    const requiresSubDamageLevelField = useMemo(() => {
        const notificationVersion = notification?.u_version?.value || process.env.REACT_APP_NOTIFICATION_VERSION
        const normalizedVersion = notificationVersion?.replace('v', '')
        const majorVersion = Number(normalizedVersion)
        /** Enquanto o form ainda não hidrata `damage_level`, usa o valor da notificação para não limpar `u_sub_damage_level` no mesmo ciclo. */
        const effectiveDamage =
            (damageLevel !== undefined && damageLevel !== null && damageLevel !== ''
                ? damageLevel
                : notification?.damage_level?.value) || ''
        return (
            requiresSubDamageLevel(effectiveDamage) &&
            Number.isFinite(majorVersion) &&
            majorVersion >= 4
        )
    }, [damageLevel, notification?.damage_level?.value, notification?.u_version?.value])

    useEffect(() => {
        if (isModerateOrSevereOrDeathDamage(damageLevel)) {
            setValue('u_number_patients_involved', variableListNumberPatientsInvolved(variableSelector)?.choices?.[1]?.value)
            setValue('u_notification_multiplier', '1')
        }

        setShowSubDamageLevel(requiresSubDamageLevelField)
        if (!requiresSubDamageLevelField) {
            setValue('u_sub_damage_level', '', { shouldDirty: true })
        }
    }, [damageLevel, requiresSubDamageLevelField, variableSelector, setValue])

    /**
     * Ao selecionar a frequencia de quando aquela notificação acontece no <inputSelect executa o calculo por aqui
     */
    useEffect(() => {
        if (uProbabilityOccurence && notification?.u_image_risk?.value) {
            let notification = notificationSelector?.notificationById
            let notificationProcessRequiredStepsRules = calculateObjectProbabilityOcurrence(notification)
            NotificationActions.defineObjectNotificationProcessRequiredStepsRules(notificationDispatch, notificationProcessRequiredStepsRules)
        }
    }, [uProbabilityOccurence])

    useEffect(() => {
        setCountLastReclassifiedIncident("0")
        if (listCountLastReclassifiedIncident && listCountLastReclassifiedIncident.length > 0) {
            if (uReclassifiedIncidentType != null && uReclassifiedIncidentType != undefined && uReclassifiedIncidentType != '') {
                for (let itemCount of listCountLastReclassifiedIncident)
                    if (itemCount.groupby_fields[0].value === uReclassifiedIncidentType)
                        setCountLastReclassifiedIncident(itemCount.stats.count)
            }
        }
    }, [uReclassifiedIncidentType, listCountLastReclassifiedIncident])


    useEffect(() => {
        if (notificationSelector.notificationCountLastReclassifiedIncidentType != null)
            setListCountLastReclassifiedIncident(notificationSelector.notificationCountLastReclassifiedIncidentType.result)
    }, [notificationSelector])

    const chooseProbabilityOccurence = (id: string) => {
        setValue('u_probability_occurence', id, { shouldDirty: true })
        setShowProbabilityOccurenceModal(false)
    }

    const chooseSubDamageLevel = (id: string) => {
        setValue('u_sub_damage_level', id, { shouldDirty: true })
        setShowSubdamageLevelInfoModal(false)
    }

    const multiplierValidation = () => {
        return watchFormData.u_number_patients_involved ===
            variableListNumberPatientsInvolved(variableSelector)
                ?.choices?.[2]?.value &&
            watchFormData.involves_patient
    }

    /**
    * Evento usado para carregar os setores de acordo com a unidade selecionada
    */
    const onChangeProbability = useCallback((id) => {
        setProbabiltyHasChanged(true)
        setValue('u_probability_occurence', id, { shouldDirty: true })
    }, [])

    useEffect(() => {
        if (notificationSelector.sentinelEvent && notification) {
            const notificationVersion = notification?.u_version?.value || process.env.REACT_APP_NOTIFICATION_VERSION
            const flat = notificationSelector.sentinelEvent
            const customSentinel = flat?.filter(item => item?.u_version == notificationVersion)?.map((item, index) => ({
                label: item.description,
                value: item.sys_id
            })).sort((o1, o2) => o1.label?.localeCompare(o2.label))
            setSentinelEvent(customSentinel.slice(14, 15)
                .concat(customSentinel.slice(0, 14)
                    .concat(customSentinel.slice(15))))
        }
    }, [notificationSelector.sentinelEvent, notification?.u_version?.value])

    useEffect(() => {
        if (notificationSelector.neverEvents && notification) {
            const notificationVersion = notification?.u_version?.value || process.env.REACT_APP_NOTIFICATION_VERSION
            const flat = notificationSelector.neverEvents;
            const neverEventss = flat?.filter(item => item?.u_version == notificationVersion || item?.sys_id == 'not_applicable')?.map((item, index) => ({
                label: item.u_description,
                value: item.sys_id
            }))

            setNeverEvents(neverEventss)
        }
    }, [notificationSelector.neverEvents, notification?.u_version?.value])

    const getNeverEvent = () => {
        return notificationSelector.notificationById?.u_never_events_reference?.value;
    }

    const getSentinelEvent = () => {
        return notificationSelector.notificationById?.u_sentinel_event_jci?.value;
    }

    useEffect(() => {
        const neverEvent = notificationSelector.notificationById?.u_never_events_reference?.value
        const sentinelEvent = notificationSelector.notificationById?.u_sentinel_event_jci?.value
        setValue('u_never_events_reference', neverEvent)
        setValue('u_sentinel_event_jci', sentinelEvent)
    }, [notificationSelector.notificationById?.u_never_events_or_sentinel_event])

    // Parte superior (com largura condicional)
    const topSection = (
        <div
            data-testid="can-edit-top"
            className="flex flex-col gap-6"
        >
            <div className="h-px bg-borderLine mb-2" />
            <BlueText>
                Informações complementares da Qualidade da Unidade
            </BlueText>
            <div>
                <LabelsForInput
                    title="Confirmar ou Reclassificar o Tipo do Incidente"
                    subtitle="Selecione a mesma opção para confirmar o Tipo de incidente notificado ou selecione outra opção para reclassificar."
                />

                <InputSelect
                    disabled={isReadOnly}
                    placeholder="Selecionar"
                    errorStyle={`${errors?.u_reclassified_incident_type
                        ? 'border border-errorText'
                        : ''
                        }`}
                    styleContainer={
                        !uReclassifiedIncidentType &&
                            !errors?.u_reclassified_incident_type
                            ? 'border border-amberAlt '
                            : ''
                    }
                    searchPlaceHolder="Buscar"
                    shouldHaveSearch={true}
                    name="u_reclassified_incident_type"
                    control={control}
                    data={
                        variableSelectorOptions(variableSelector)
                            ?.reclassifiedIncidentTypeSelect
                    }
                    defaultValue={
                        notification?.u_reclassified_incident_type
                            ?.value
                    }
                />
                {(countLastReclassifiedIncident != null &&
                    (<Label className={`ml-1 mt-${errors?.u_reclassified_incident_type ? '4' : '1'} text-darkBlue/80 font-normal whitespace-normal`}>
                        {countLastReclassifiedIncident === `0` ?
                            `Não houve nenhuma notificação` : (countLastReclassifiedIncident === `1` ?
                                <>Foi registrada <b>{countLastReclassifiedIncident}</b> notificação</>
                                :
                                <>Foram registradas <b>{countLastReclassifiedIncident}</b> notificações</>
                            )}
                        &nbsp;com este tipo de reclassificação de incidente nos últimos 30 dias
                    </Label>))}

            </div>
            <div>
                <InputSelect
                    disabled={isReadOnly}
                    placeholder="Selecionar"
                    errorStyle={`${errors?.u_oms_classification
                        ? 'border border-errorText'
                        : ''
                        }`}
                    styleContainer={
                        !uOmsClassification &&
                            !errors?.u_oms_classification
                            ? 'border border-amberAlt '
                            : ''
                    }
                    searchPlaceHolder="Buscar"
                    styleBoxOptions="mt-3.5"
                    label="Classificação do incidente de acordo com a OMS - Organização Mundial de Saúde"
                    name="u_oms_classification"
                    control={control}
                    data={
                        variableSelectorOptions(variableSelector)
                            ?.omsClassifications
                    }
                    defaultValue={
                        notification?.u_oms_classification?.value
                    }
                />
            </div>
            {
                notification?.u_version?.value?.toLowerCase() !== 'v1'
                && (<>
                    <div>
                        <div className="flex text-right items-center pb-1">
                            <Label className="mr-2 pb-0">
                                Com que frequência este evento ocorre na unidade?
                            </Label>
                            <div className="flex items-center cursor-pointer" onClick={() => { setShowProbabilityOccurenceModal(true) }}>
                                <YellowCircleInterrogation />
                                <span className="ml-2 text-sm underline font-semibold text-blueTheme">
                                    Saiba mais
                                </span>
                            </div>
                        </div>

                        <InputSelect
                            disabled={isReadOnly}
                            onChange={onChangeProbability}
                            placeholder="Selecionar"
                            errorStyle={`${errors?.u_probability_occurence
                                ? 'border border-errorText'
                                : ''
                                }`}
                            styleContainer={
                                !uProbabilityOccurence &&
                                    !errors?.u_probability_occurence
                                    ? 'border border-amberAlt '
                                    : ''
                            }
                            searchPlaceHolder="Buscar"
                            styleBoxOptions="mt-3.5"
                            name="u_probability_occurence"
                            control={control}
                            data={occurrencesList()}
                            defaultValue={
                                notification?.u_probability_occurence?.value
                            }
                        />

                        <DefaultModal
                            isOpen={showProbabilityOccurenceModal && !isMobile}
                            handleCloseModal={() => {
                                setShowProbabilityOccurenceModal(false)
                            }}
                            children={<ProbabilityOccurenceModal
                                chosenElementId={uProbabilityOccurence}
                                chooseProbabilityOccurence={chooseProbabilityOccurence}
                                close={() => {
                                    setShowProbabilityOccurenceModal(false)
                                }}
                                isReadOnly={isReadOnly}
                            />}
                        />

                        {showSubDamageLevel &&
                            <GenericRadioGroup
                                title="Subclassificação do Incidente com Dano"
                                name="u_sub_damage_level"
                                control={control}
                                register={register}
                                styleContainer={'mt-5 '}
                                inputClassNames={`${!errors?.u_sub_damage_level
                                    ? 'border border-amberAlt'
                                    : ''
                                    } ${errors?.u_sub_damage_level && 'border border-errorText'}`}
                                errors={errors?.u_sub_damage_level}
                                required={showSubDamageLevel}
                                options={SUB_DAMAGE_LEVEL_OPTIONS}>
                                <div className="flex items-center cursor-pointer" onClick={() => { setShowSubdamageLevelInfoModal(true) }}>
                                    <YellowCircleInterrogation />
                                    <span className="ml-2 text-sm underline font-semibold text-blueTheme">
                                        Saiba mais
                                    </span>
                                </div>
                            </GenericRadioGroup>
                        }

                        {showSubdamageLevelInfoModal && !isMobile &&
                            (<DefaultModal
                                isOpen={true}
                                handleCloseModal={() => {
                                    setShowSubdamageLevelInfoModal(false)
                                }}
                                children={<SubDamageLevelInfoModal
                                    chosenElementId={uSubDamageLevel}
                                    close={() => {
                                        setShowSubdamageLevelInfoModal(false)
                                    }}
                                    chooseSubDamageLevel={chooseSubDamageLevel}
                                />}
                            />)
                        }

                        <DefaultMobileModal
                            closeModal={() => {
                                setShowProbabilityOccurenceModal(false)
                            }}
                            isOpen={showProbabilityOccurenceModal}
                            children={<ProbabilityOccurenceModal
                                chosenElementId={uProbabilityOccurence}
                                chooseProbabilityOccurence={chooseProbabilityOccurence}
                                close={() => {
                                    setShowProbabilityOccurenceModal(false)
                                }}
                                isReadOnly={isReadOnly}
                            />}
                        />

                    </div>
                </>)
            }
        </div>
    );

    // Parte inferior (sempre largura máxima)
    const bottomSection = (
        <div
            data-testid="can-edit-bottom"
            className="flex flex-col gap-6"
        >
            <RiskEventRadio
                readOnly={isReadOnly}
                inputClassNames={`${!notification?.u_image_risk?.value && !errors.u_image_risk
                    ? 'border border-amberAlt'
                    : ''
                    } ${errors.u_image_risk && 'border border-errorText'}`}
                errors={errors.u_image_risk}
                register={register}
                notification={notification}
            />

            {
                notification?.u_version?.value?.toLowerCase() !== 'v1' && (
                    <>
                        <NeverEventsOrSentinelEventRadio
                            readOnly={isReadOnly}
                            inputClassNames={`${!notification?.u_never_events_or_sentinel_event?.value && !errors.u_never_events_or_sentinel_event
                                ? 'border border-amberAlt'
                                : ''
                                } ${errors.u_never_events_or_sentinel_event && 'border border-errorText'}`}
                            errors={errors.u_never_events_or_sentinel_event}
                            register={register}
                            notification={notification}
                        />

                        {
                            uNeverOrSentinelEvents === 'yes' &&
                            <>
                                <div>
                                    <Label>Never Events - ANVISA</Label>
                                    <HelpDescription>
                                        Os ”never events” são eventos adversos que nunca deveriam ocorrer em serviços de saúde, considerados prioritários pela ANVISA para a notificação e investigação.
                                    </HelpDescription>
                                    <InputSelect
                                        disabled={isReadOnly}
                                        defaultValue={getNeverEvent()}
                                        searchPlaceHolder="Buscar"
                                        errorTextStyle='-bottom-[18px]'
                                        required={true}
                                        styleBoxOptions="top-[55px]"
                                        name="u_never_events_reference"
                                        control={control}
                                        data={neverEvents}
                                        placeholder="Selecione"
                                        errorStyle={`${errors.u_never_events_reference ? inputRequiredStyle : ''}`}
                                        styleContainer={
                                            !uNeverEventsReference &&
                                                !errors?.u_reclassified_incident_type
                                                ? 'mt-2 border border-amberAlt '
                                                : 'mt-2'
                                        }
                                    />
                                </div>
                                <div>
                                    <Label>Evento Sentinela - JCI {' '}
                                        <span className='font-normal'>(Campo destinado apenas para Unidades acreditadas na Joint Comission International)</span>
                                    </Label>
                                    <HelpDescription>
                                        Um evento sentinela é um evento de segurança do paciente (não principalmente relacionado ao curso natural da doença ou condição subjacente do paciente) que atinge um paciente.
                                    </HelpDescription>
                                    <InputSelect
                                        disabled={isReadOnly}
                                        defaultValue={getSentinelEvent()}
                                        searchPlaceHolder="Buscar"
                                        errorTextStyle='-bottom-[18px]'
                                        required={false}
                                        styleBoxOptions="top-[55px]"
                                        name="u_sentinel_event_jci"
                                        control={control}
                                        data={sentinelEvent}
                                        placeholder="Selecione"
                                        errorStyle={`${errors.u_sentinel_event_jci ? inputRequiredStyle : ''
                                            }`}
                                        styleContainer={
                                            !uSentinelEventJci &&
                                                !errors?.u_reclassified_incident_type
                                                ? 'mt-2 border border-amberAlt '
                                                : 'mt-2'
                                        }
                                    />
                                </div>
                            </>
                        }
                    </>
                )
            }

            <div className="h-px bg-borderLine mt-6" />
        </div>
    );

    // Parte de "Informações da notificação" (sempre largura máxima)
    const notificationInfoSection = (
        <fieldset disabled={isReadOnly}>
            <BlueText>Informações da notificação</BlueText>

            <div className="grid lg:grid-cols-4 mt-6 gap-x-4 gap-y-6">
                {
                    notification?.u_version?.value?.toLowerCase() === 'v1' && (
                        <div className="md:col-span-4 col-span-4">
                            <InputInfoLabelSubTitle>
                                Tipo do Incidente informado pelo notificante
                            </InputInfoLabelSubTitle>
                            <InputInfoLabelTitle className="text-sm">
                                {notification?.incident_type?.display_value}
                            </InputInfoLabelTitle>
                        </div>
                    )
                }

                <div className="md:col-span-1 col-span-4">
                    <CustomDatePicker
                        disabled={statusIsPendingFinishOrFinished || !canEditStepOne}
                        name="date_incident"
                        control={control}
                        required
                        label="Data da ocorrência"
                        placeholderData="Selecionar"
                        dateValue={
                            new Date(notification?.date_incident?.value)
                        }
                        maxDate={new Date()}
                    />
                </div>
                <InputLabel
                    container="md:col-span-3 col-span-4"
                    label="Título do incidente"
                    defaultValue={
                        notification?.incident_title?.display_value
                    }
                    name="incident_title"
                    error={{
                        message: errors?.incident_title ? 'Campo é obrigatório' : ''
                    }}
                    register={register}
                    maxLength={80}
                />
                <div className="md:col-span-2 col-span-4">
                    <InputSelect
                        placeholder="Selecionar"
                        searchPlaceHolder="Buscar"
                        shouldHaveSearch={true}
                        styleBoxOptions="mt-3.5"
                        label="Unidade"
                        name="location_incident"
                        errorStyle={`${errors?.location_incident
                            ? 'border border-errorText'
                            : ''
                            }`}
                        control={control}
                        data={locationOptions}
                        defaultValue={
                            notification?.location_incident?.value
                        }
                    />
                </div>

                <div className="md:col-span-2 col-span-4">
                    <InputSelect
                        placeholder="Selecionar"
                        searchPlaceHolder="Buscar"
                        shouldHaveSearch={true}
                        styleBoxOptions="mt-3.5"
                        label="Setor"
                        name="incident_sector"
                        errorStyle={`${errors?.incident_sector
                            ? 'border border-errorText'
                            : ''
                            }`}
                        control={control}
                        data={incidentSectorList(
                            sectorSelector,
                            locationIncident
                        )}
                        defaultValue={notification?.incident_sector?.value}
                    />
                </div>
                <div className="md:col-span-2 col-span-4">
                    <InputSelect
                        placeholder="Selecionar"
                        searchPlaceHolder="Buscar"
                        styleBoxOptions="mt-3.5"
                        label="Turno"
                        name="shift_incident"
                        errorStyle={`${errors?.shift_incident
                            ? 'border border-errorText'
                            : ''
                            }`}
                        control={control}
                        data={shiftIncidentList(variableSelector)}
                        defaultValue={notification?.shift_incident?.value}
                    />
                </div>
                <div className="md:col-span-2 col-span-4">
                    <InputSelect
                        placeholder="Selecionar"
                        searchPlaceHolder="Buscar"
                        shouldHaveSearch={true}
                        styleBoxOptions="mt-3.5"
                        label="Setor do notificante"
                        name="reporter_sector"
                        errorStyle={`${errors?.reporter_sector
                            ? 'border border-errorText'
                            : ''
                            }`}
                        control={control}
                        data={incidentSectorList(
                            sectorSelector,
                            locationIncident
                        )}
                        defaultValue={notification?.reporter_sector?.value}
                    />
                </div>
                <div className="col-span-4">
                    <TextArea
                        label="Descrição do incidente"
                        defaultValue={
                            notification?.incident_description
                                ?.display_value
                        }
                        name="incident_description"
                        register={register}
                        error={{
                            message: errors?.incident_description ? 'Campo é obrigatório' : ''
                        }}
                        className={textAreaStyles}
                        placeholder="Digite aqui"
                        rows={8}
                    />
                </div>
                <div className="col-span-4">
                    <TextArea
                        label="Ação corretiva imediada"
                        defaultValue={
                            notification?.immediate_corrective_action
                                ?.display_value
                        }
                        name="immediate_corrective_action"
                        error={{
                            message: errors?.immediate_corrective_action ? 'Campo é obrigatório' : ''
                        }}
                        register={register}
                        className={textAreaStyles}
                        placeholder="Digite aqui"
                        rows={8}
                    />
                </div>
                {
                    notification?.u_version?.value?.toLowerCase() !== 'v1' && (
                        <div className="md:col-span-2 col-span-2">
                            <InputInfoLabelSubTitle>
                                Tipo do Incidente informado pelo notificante
                            </InputInfoLabelSubTitle>
                            <InputInfoLabelTitle className="text-sm">
                                {notification?.incident_type?.display_value}
                            </InputInfoLabelTitle>
                        </div>
                    )
                }
                <div className={`${notification?.u_version?.value?.toLowerCase() !== 'v1' ? 'col-span-2' : 'col-span-4 lg:w-1/2'} relative`}>
                    <span className="absolute text-xs text-darkGray left-28">
                        (opcional)
                    </span>
                    <InputSelect
                        required
                        placeholder="Selecionar"
                        searchPlaceHolder="Buscar"
                        shouldHaveSearch={true}
                        styleBoxOptions="mt-3.5"
                        label="Fator contribuinte"
                        name="u_categorized_contributing_factor"
                        control={control}
                        data={variablesListContributingFactor(
                            variableSelector
                        )}
                        defaultValue={
                            notification?.u_categorized_contributing_factor
                                ?.value
                        }
                    />
                </div>

                <div className="col-span-4 lg:w-1/2">
                    <InputSelect
                        placeholder="Selecionar"
                        searchPlaceHolder="Buscar"
                        styleBoxOptions="mt-3.5"
                        label="Classificação do Incidente"
                        name="damage_level"
                        errorStyle={`${errors?.damage_level
                            ? 'border border-errorText'
                            : ''
                            }`}
                        required
                        control={control}
                        data={variablesListDamageLevel(variableSelector, notification?.damage_level?.value)}
                        defaultValue={notification?.damage_level?.value}
                        onChange={onChangeDamageLevel}
                    />
                </div>
                <div className={`col-span-4`}>
                    <CustomToggle
                        control={control}
                        name={'involves_patient'}
                        enabled={involvesPatient || false}
                        setEnabled={setLocalEnabled}
                        label="O incidente está relacionado a algum paciente?"
                        leftText="Não"
                        rightText="Sim"
                    />
                </div>

                <div className="md:col-span-2 col-span-4">
                    <InvolvedPatients
                        notification={notification}
                        errors={errors}
                        register={register}
                        variablePatientsInvolved={variableListNumberPatientsInvolved(
                            variableSelector
                        )}
                        watchFormData={watchFormData as NotifyUpdateEventType}
                    />
                </div>

                <InvolvesPatient
                    watch={watch}
                    control={control}
                    errors={errors}
                    variableSelector={variableSelector}
                    statusIsArchivedOrFinished={statusIsPendingFinishOrFinished}
                    setValue={setValue}
                    register={register}
                    notification={notification}
                    isProfileCorporateQualityBoard={
                        isReadOnly
                    }
                    canEditStepOne={canEditStepOne}
                    involvesPatient={involvesPatient}
                    involvePatientMoreThenOne={
                        watchFormData?.u_number_patients_involved ===
                        variableListNumberPatientsInvolved(variableSelector)
                            ?.choices?.[2]?.value
                    }
                />

                <div className="md:col-span-2 col-span-4">
                    <Multiplier
                        notification={notification}
                        register={register}
                        setValue={setValue}
                        showMultiplier={multiplierValidation()}
                        value={watch('u_notification_multiplier')}
                    />
                </div>
            </div>
        </fieldset>
    );

    if (showTopSectionOnly) {
        return <>{topSection}</>;
    }

    return (
        <>
            {bottomSection}
            {notificationInfoSection}
        </>
    )
}
